import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, PlayCircle, Phone } from "lucide-react";
import { COMPANY, HERO_IMAGE } from "../mock";

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const glowY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section id="home" ref={ref} data-testid="hero-section" className="relative bg-[#faf7f2] overflow-hidden">
      <motion.div
        style={{ y: glowY }}
        className="absolute -top-32 -right-32 w-[560px] h-[560px] rounded-full bg-[radial-gradient(circle,rgba(200,16,46,0.14),transparent_65%)]"
      />
      <div className="absolute top-1/2 -left-40 w-[420px] h-[420px] rounded-full bg-[radial-gradient(circle,rgba(27,122,70,0.10),transparent_65%)]" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20 lg:py-28">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <motion.span
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="inline-flex items-center gap-2 bg-[#1b7a46]/10 text-[#0e4a2c] text-xs font-semibold uppercase tracking-widest px-4 py-1.5 rounded-full mb-7"
            >
              <span className="w-2 h-2 rounded-full bg-[#1b7a46]" />
              {COMPANY.productName} — {COMPANY.area.split(" e tutta")[0]}
            </motion.span>

            <h1 data-testid="hero-title" className="font-condensed font-black uppercase tracking-tight leading-[0.92] text-[#191512] text-6xl sm:text-7xl lg:text-8xl">
              <MaskedLine delay={0.15}>Tranquilli</MaskedLine>
              <MaskedLine delay={0.3} className="text-[#c8102e] italic">
                Ci pensa
              </MaskedLine>
              <MaskedLine delay={0.45}>
                Napolano<span className="text-[#1b7a46]">!</span>
              </MaskedLine>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.75 }}
              className="mt-7 text-base sm:text-lg text-slate-600 leading-relaxed max-w-xl"
            >
              Un rubinetto che perde, una presa da sistemare, il condizionatore da controllare prima dell'estate.
              Con la {COMPANY.productName} di {COMPANY.brandName} hai un tecnico di fiducia sempre a disposizione,
              con un semplice abbonamento mensile. Tu ci pensi una volta sola: ci pensa Napolano.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.9 }}
              className="mt-9 flex flex-col sm:flex-row gap-4"
            >
              <a
                href="#pacchetti"
                data-testid="hero-primary-cta"
                className="inline-flex items-center justify-center gap-2 bg-[#c8102e] hover:bg-[#a50d26] text-white font-semibold px-8 py-4 rounded-full shadow-lg shadow-[#c8102e]/30 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl"
              >
                Scegli il tuo pacchetto
                <ArrowRight className="w-5 h-5" />
              </a>
              <a
                href="#come-funziona"
                data-testid="hero-secondary-cta"
                className="inline-flex items-center justify-center gap-2 border-2 border-[#191512] text-[#191512] hover:bg-[#191512] hover:text-white font-semibold px-8 py-4 rounded-full transition-colors duration-200"
              >
                <PlayCircle className="w-5 h-5" />
                Scopri come funziona
              </a>
            </motion.div>

            <motion.a
              href={`tel:${COMPANY.phone.replace(/\s/g, "")}`}
              data-testid="hero-phone-link"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.7, delay: 1.05 }}
              className="mt-7 inline-flex items-center gap-2.5 text-sm font-semibold text-[#0e4a2c] hover:text-[#1b7a46] transition-colors duration-200"
            >
              <span className="flex items-center justify-center w-9 h-9 rounded-full bg-[#1b7a46]/10">
                <Phone className="w-4 h-4" />
              </span>
              {COMPANY.phone} — chiamaci, rispondiamo noi
            </motion.a>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="absolute -top-8 -left-8 w-32 h-32 rounded-full border-[10px] border-[#c8102e]/15" />
            <div className="absolute -bottom-10 -right-10 w-44 h-44 rounded-full bg-[#1b7a46]/12" />
            <div className="relative rounded-[2rem] overflow-hidden shadow-2xl border-[10px] border-white">
              <motion.img
                src={HERO_IMAGE}
                alt="Tecnico Napolano Impianti al lavoro"
                data-testid="hero-image"
                style={{ y: imgY }}
                className="w-full h-[340px] sm:h-[440px] lg:h-[540px] object-cover scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#191512]/35 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-4 right-4 sm:right-auto bg-white/95 backdrop-blur rounded-2xl px-5 py-4 shadow-lg flex items-center gap-3 animate-float-soft">
                <span className="flex items-center justify-center w-10 h-10 rounded-full bg-[#c8102e]">
                  <ArrowRight className="w-5 h-5 text-white" />
                </span>
                <span>
                  <span className="block font-condensed font-bold uppercase text-[#191512] text-lg leading-none">Tecnici di fiducia</span>
                  <span className="block text-xs text-slate-500 mt-1">{COMPANY.area}</span>
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
